import { useCallback, useEffect, useState } from 'react'
import { WITHHELD } from '../api/answer'
import { Withheld } from './Withheld'
import type { Api } from '../api'
import { ApiError, NO_RECV_FILTERS, USD } from '../api/types'
import type { ApiFailure, Currency, RecvFilters, TransactionPage, TransactionRow } from '../api/types'
import { Button, EmptyState, Loading, money, currencyNote } from './ui'
import { Explain, ExplainMark } from './DevModeContext'
import { FailureState } from './FailureState'
import type { ExportDoc, ExportRow } from '../export/model'
import { useSortMirror } from './gridSort'
import { GridChrome, ResizeHandle, useGridLayout } from './gridLayout'

/**
 * AGING BY TRANSACTION — the Fabric report's flat detail page.
 *
 * Twenty-one columns, one row per open document, in the report's own column
 * order (read from the .pbix layout, not from memory).
 *
 * ---------------------------------------------------------------------------
 * IT PAGES, AND THE PAGER IS PART OF THE SECURITY MODEL
 *
 * This is the only screen that returns raw documents rather than totals. The
 * server pages it and clamps the page size to 500 — the clamp lives in the
 * database, not here, so it holds however the procedure is reached.
 *
 * Each page is one audited read. Somebody pulling the whole book leaves a
 * hundred rows in the access log instead of one, which is the point: paging
 * does not prevent extraction, it makes the attempt visible.
 *
 * SORTING IS SERVER-SIDE, for the same reason. Sorting the current page in the
 * browser would sort 200 rows out of 19,000 and look exactly like sorting the
 * report — the single most convincing wrong answer this screen could give.
 */

type Align = 'left' | 'right'

interface Column {
  key: keyof TransactionRow
  header: string
  align: Align
  /** The @SortBy value the procedure recognises. Omitted where sorting is not offered. */
  sort?: string
  width?: number
  render?: (r: TransactionRow) => string
}

const HIDDEN = '—' // em dash: "you were not shown this"

/* Names this grid's layout for the session. The manager's screen: twenty-one
   columns, 1,964px of them past the right edge of a laptop. */
const GRID_ID = 'receivables-transactions'

/** Dates arrive as ISO strings; the source has no time component that matters. */
function day(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Row level, so the currency is never ambiguous here: one document is booked in
 * exactly one currency. This is the page where the code matters most — a grid
 * of documents from six countries, all showing bare numbers, invites the reader
 * to compare figures that are not comparable.
 */
function measure(v: number | null, currency: Currency, code?: string | null): string {
  return v === null ? HIDDEN : money(v, currency, code)
}

function count(v: number | null): string {
  return v === null ? HIDDEN : String(v)
}

/** The report's twenty-one columns, in its order. */
const COLUMNS: Column[] = [
  { key: 'businessUnit', header: 'Business Unit', align: 'left', sort: 'BusinessUnit', width: 130 },
  { key: 'businessTeam', header: 'Business Team', align: 'left', sort: 'BusinessTeam', width: 130 },
  { key: 'salesperson', header: 'Salesperson', align: 'left', sort: 'Salesperson', width: 170 },
  { key: 'customer', header: 'Customer', align: 'left', sort: 'Customer', width: 220 },
  { key: 'paymentTerms', header: 'Payment Terms', align: 'left', sort: 'PaymentTerms', width: 130 },
  { key: 'docNumber', header: 'Document', align: 'left', sort: 'DocNumber', width: 130 },
  { key: 'documentDate', header: 'Document Date', align: 'left', sort: 'DocumentDate', width: 130, render: (r) => day(r.documentDate) },
  { key: 'daysOld', header: 'Days Old', align: 'right', sort: 'DaysOld', width: 95, render: (r) => count(r.daysOld) },
  { key: 'dueDate', header: 'Due Date', align: 'left', sort: 'DueDate', width: 130, render: (r) => day(r.dueDate) },
  { key: 'daysOverdue', header: 'Days Overdue', align: 'right', sort: 'DaysOverdue', width: 115, render: (r) => count(r.daysOverdue) },
  { key: 'advancePayment', header: 'Advance Payment', align: 'right', sort: 'AdvancePayment', width: 145 },
  { key: 'totalReceivables', header: 'Total Receivables', align: 'right', sort: 'TotalReceivables', width: 150 },
  { key: 'totalOverdue', header: 'Total Overdue', align: 'right', sort: 'TotalOverdue', width: 140 },
  { key: 'over30DaysOverdue', header: 'Over 30 Days (Overdue)', align: 'right', sort: 'Over30DaysOverdue', width: 175 },
  { key: 'd0To30', header: '0 To 30 Days', align: 'right', sort: 'D0To30', width: 135 },
  { key: 'd31To60', header: '31 To 60 Days', align: 'right', sort: 'D31To60', width: 140 },
  { key: 'd61To90', header: '61 To 90 Days', align: 'right', sort: 'D61To90', width: 140 },
  { key: 'd91To120', header: '91 To 120 Days', align: 'right', sort: 'D91To120', width: 145 },
  { key: 'd121To180', header: '121 To 180 Days', align: 'right', sort: 'D121To180', width: 150 },
  { key: 'd181To365', header: '181 To 365 Days', align: 'right', sort: 'D181To365', width: 150 },
  { key: 'dOver365', header: 'Over 365 Days', align: 'right', sort: 'DOver365', width: 145 },
]

/* The columns the column rules govern (055): under the new model a blank one is
   withheld on that document, while a blank date or reference was never recorded (M8). */
const WITHHOLDABLE = new Set<string>([
  'salesperson', 'paymentTerms', 'daysOld', 'daysOverdue', 'advancePayment', 'totalReceivables', 'totalOverdue',
  'over30DaysOverdue', 'd0To30', 'd31To60', 'd61To90', 'd91To120', 'd121To180', 'd181To365', 'dOver365',
])

const PAGE_SIZE = 100

export function TransactionsTable({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
  filters = NO_RECV_FILTERS,
  exportRef,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
  /** The page's standing filter bar. It narrows the SERVER's population, so
      `total` and the paging footer count the filtered book, not the whole one. */
  filters?: RecvFilters
  /** The page's card header renders the extract buttons; this hands it the
      builder for the current page and sort. Written every render, read at click. */
  exportRef?: { current: (() => ExportDoc | null) | null }
}) {
  const [data, setData] = useState<TransactionPage | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState<string | undefined>(undefined)
  const [sortDesc, setSortDesc] = useState(true)
  const layout = useGridLayout(GRID_ID, COLUMNS)
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(GRID_ID, { key: sortBy ?? null, desc: sortDesc }, (s) => {
    /* The key IS the procedure's own @SortBy value. One it does not know is
       clamped server-side and recorded as a probe, so it never leaves here. */
    const known = s.key !== null && COLUMNS.some((c) => c.sort === s.key)
    setSortBy(known ? s.key ?? undefined : undefined)
    setSortDesc(known ? s.desc : true)
    setPage(1) // a new order means page 1 holds different rows
  })
  const shown = layout.columns
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const { data: result, vis } = await api.receivablesTransactions({
        page,
        pageSize: PAGE_SIZE,
        sortBy,
        sortDesc,
        currency,
        filters,
      })
      setData(result)
      setV2(vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, page, sortBy, sortDesc, currency, filters, viewAs])

  useEffect(() => {
    void load()
  }, [load])

  // Whose eyes we look through changes every row, so go back to page 1 — page
  // 40 of one person's book is not page 40 of another's, and landing there
  // would show a stranger's documents under a heading that says otherwise.
  useEffect(() => {
    setPage(1)
  }, [viewAs, currency, filters])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />

  const total = data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const last = Math.min(page * PAGE_SIZE, total)

  const onSort = (col: Column) => {
    if (!col.sort) return
    if (sortBy === col.sort) setSortDesc((d) => !d)
    else {
      setSortBy(col.sort)
      setSortDesc(true)
    }
    setPage(1) // a new order means page 1 holds different rows
  }

  /* THE EXTRACT — the page in front of the reader, not the book: the same
     rows in the same server order, dates exactly as printed. The pager line
     rides along as context AND as the table note, because a hundred rows
     without it would read as the whole result. */
  const buildDoc = (): ExportDoc | null => {
    if (loading || !data || data.rows.length === 0) return null
    const sortCol = COLUMNS.find((c) => c.sort === sortBy)
    const pageLine = `Page ${page} of ${money(lastPage)} — ${money(total)} documents in the full result`
    return {
      report: 'Receivables',
      page: 'By Transaction',
      title: 'Open documents',
      subtitle: 'One row per document, in the report’s twenty-one columns.',
      context: [
        `Currency: ${currencyNote(currency)}`,
        sortCol
          ? `Sorted by ${sortCol.header}, ${sortDesc ? 'descending' : 'ascending'} — ordered on the server across every document`
          : 'Sort: the server’s default order',
        pageLine,
      ],
      privileged: viewAs,
      currency,
      tables: [
        {
          // A right-aligned column with a renderer is a day count; without one
          // it is money. Dates keep their on-screen text, so the file and the
          // grid can never disagree about what "04 Aug 2026" was.
          /* `shown`, not COLUMNS: the extract is the screen, so a reader who
             hid or reordered columns gets the file they are looking at. */
          columns: shown.map((c) => ({
            header: c.header,
            kind: c.align === 'right' ? (c.render ? ('int' as const) : ('money' as const)) : ('text' as const),
          })),
          rows: data.rows.map<ExportRow>((r) => ({
            cells: shown.map((c) => {
              const raw = r[c.key]
              if (raw === null) return v2 && WITHHOLDABLE.has(String(c.key)) ? WITHHELD : null
              if (c.align === 'right') return raw as number
              return c.render ? c.render(r) : String(raw)
            }),
          })),
          note: pageLine,
        },
      ],
    }
  }

  // Hand the current builder up to the page's card header (chrome above the
  // scroll region), reclaiming the row the extract buttons used to occupy.
  if (exportRef) exportRef.current = buildDoc

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <GridChrome api={layout} />
      {/* The pager below stays put; only the grid scrolls. */}
      <div className="scroll-slim relative min-h-0 flex-1 overflow-auto">
        {/* The overlay keeps the previous page visible while the next loads.
            Blanking the grid on every page turn makes a 200ms request feel like
            a page reload. */}
        {loading && data && (
          <div className="pointer-events-none absolute inset-0 z-30 bg-(--color-surface)/60" />
        )}

        {/* border-SEPARATE so a pinned column cannot paint out the row lines,
            and min-w-max rather than a fixed 2100px so the table actually
            narrows when the reader puts columns away. */}
        <table className="report-table w-full min-w-max border-separate border-spacing-0 text-(length:--text-body)">
          <thead>
            <tr>
              {shown.map((c) => {
                const active = sortBy === c.sort
                const pin = layout.pinOf(c.key)
                const w = layout.widthOf(c.key) ?? c.width
                return (
                  <th
                    key={String(c.key)}
                    ref={layout.headerRef(c.key)}
                    scope="col"
                    aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : 'none'}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', c.key)
                      setDragKey(c.key)
                    }}
                    onDragOver={(e) => {
                      if (!dragKey || dragKey === c.key) return
                      e.preventDefault()
                      setOverKey(c.key)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                      setDragKey(null)
                      setOverKey(null)
                    }}
                    onDragEnd={() => {
                      setDragKey(null)
                      setOverKey(null)
                    }}
                    style={{
                      width: w,
                      minWidth: w,
                      // A floor for a figure column, a cap only for text (4 Oct 2026).
                      maxWidth: c.align === 'right' ? undefined : w,
                      left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                      right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                    }}
                    className={`relative sticky top-0 border-b border-(--color-line-strong) px-3 py-2.5 text-center font-semibold text-(--color-ink) ${
                      pin === 'left'
                        ? 'z-30 border-r border-(--color-line)'
                        : pin === 'right'
                          ? 'z-30 border-l border-(--color-line)'
                          : 'z-20'
                    } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                  >
                    {/* Sorting keeps the click; the explanation rides beside it
                        as a mark, because a button inside a button is invalid
                        HTML and a column that stopped sorting the day somebody
                        documented it would be a strange bug to have to explain. */}
                    <span data-head-label className="inline-block whitespace-nowrap">
                    {c.sort ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onSort(c)}
                          className={`whitespace-nowrap transition-colors hover:text-(--color-accent) ${
                            c.align === 'right' ? 'text-right' : 'text-left'
                          } ${active ? 'text-(--color-accent)' : ''}`}
                          title={`Sort by ${c.header}`}
                        >
                          {c.header}
                          {active && <span aria-hidden> {sortDesc ? '▼' : '▲'}</span>}
                        </button>
                        <ExplainMark surface="transactions" measureKey={String(c.key)} label={c.header} />
                      </>
                    ) : (
                      <Explain surface="transactions" measureKey={String(c.key)}>
                        {c.header}
                      </Explain>
                    )}
                    </span>
                    <ResizeHandle
                      label={c.header}
                      widthOf={() => layout.headerWidth(c.key)}
                      minWidth={() => layout.minWidthOf(c.key)}
                      onResize={(px) => layout.resize(c.key, px)}
                      onAutoFit={() => layout.autoFit(c.key)}
                    />
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {data?.rows.map((r, i) => (
              <tr
                // Doc_Number is unique per row in the source, but a null one is
                // possible; the index keeps React stable either way.
                key={`${r.docNumber ?? 'blank'}-${i}`}
                className="group transition-colors hover:bg-(--color-navy-50)"
              >
                {shown.map((c) => {
                  const raw = r[c.key]
                  const text = v2 && raw === null && WITHHOLDABLE.has(String(c.key))
                    ? <Withheld />
                    : c.render
                    ? c.render(r)
                    : typeof raw === 'number'
                      ? measure(raw, currency, r.currencyCode)
                      : raw === null
                        ? HIDDEN
                        : String(raw)

                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key) ?? c.width
                  return (
                    <td
                      key={String(c.key)}
                      title={typeof text === 'string' && text.length > 0 ? text : undefined}
                      style={{
                        width: w,
                        // A figure is never cut (4 Oct 2026): its width is a floor.
                        maxWidth: c.align === 'right' ? undefined : w,
                        left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                        right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                      }}
                      /* The row border is on the CELL, not the row: the table is
                         border-separate so a pinned cell — its own compositor
                         layer — cannot paint the row's line out from over it.
                         A pinned cell also repeats the row's hover tint by hand,
                         because that is painted on the <tr> underneath it.
                         Only TEXT truncates; a figure stays whole on one line. */
                      className={`${c.align === 'right' ? 'whitespace-nowrap' : 'truncate'} border-b border-(--color-line) px-3 py-2 ${
                        c.align === 'right' ? 'num text-(--color-ink)' : 'text-(--color-ink-2)'
                      } ${
                        pin
                          ? `sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-navy-50) ${
                              pin === 'left' ? 'border-r' : 'border-l'
                            } border-(--color-line)`
                          : ''
                      }`}
                    >
                      {/* A null STRING column is masked (withheld); an empty one
                          is genuinely blank. Both render quietly, but only the
                          masked one gets the dash. */}
                      {text === '' ? <span className="text-(--color-ink-4)">—</span> : text}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* On EVERY fetch: sorting, paging and the currency toggle each replace
            every figure in the table. */}
        {loading && <Loading label="Loading documents…" />}

        {!loading && data && data.rows.length === 0 && (
          <EmptyState
            title={viewAs ? `${viewAs} has no open documents` : 'No open documents'}
            detail="Nothing matches the current filter. This is an empty result, not a refusal — a refusal would say so and give you a reference."
          />
        )}
      </div>

      {/* ---------------- Pager ---------------- */}
      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-(--color-line) bg-(--color-surface-2) px-5 py-3">
          <p className="text-(length:--text-small) text-(--color-ink-3)">
            <span className="num font-semibold text-(--color-ink-2)">{money(first)}</span>–
            <span className="num font-semibold text-(--color-ink-2)">{money(last)}</span> of{' '}
            <span className="num font-semibold text-(--color-ink-2)">{money(total)}</span> documents
          </p>

          <div className="flex items-center gap-2">
            <Button size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="px-1 text-(length:--text-small) text-(--color-ink-3)">
              Page <span className="num font-semibold text-(--color-ink-2)">{page}</span> of{' '}
              <span className="num font-semibold text-(--color-ink-2)">{money(lastPage)}</span>
            </span>
            <Button size="sm" disabled={page >= lastPage || loading} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
